import React, { useState } from 'react';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuItem, useSidebar } from 'components/ui/sidebar';
import { LayoutDashboard, Users, Menu, LogOut, FileSearch, ClipboardList, Settings, ListChecks } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { toast } from 'sonner';
import Text from '../Text';
import { COLORS, fontSizes } from 'appconstants';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ConfirmModal from '../ConfirmModal';
import { setUser } from 'store/slices/common-slice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store/store';
import { ReactSVG } from 'react-svg';
import { audits_icon, customers_icon, dashboard_icon, forms_icon, logo_icon, settings_icon, users_icon, white_audits_icon, white_customers_icon, white_dashboard_icon, white_forms_icon, white_settings_icon, white_users_icon } from 'asset/icons';

const sidebarMenu = [
  { name: 'Dashboard', url: '/', icon: dashboard_icon, active_icon: white_dashboard_icon },
  { name: 'Manage Users', url: '/users', icon: users_icon, active_icon: white_users_icon },
  { name: 'Manage Customers', url: '/customers', icon: customers_icon, active_icon: white_customers_icon },
  { name: 'Manage Audits', url: '/audits', icon: audits_icon, active_icon: white_audits_icon },
  { name: 'Forms', url: '/forms', icon: forms_icon, active_icon: white_forms_icon },
  { name: 'Settings', url: '/settings', icon: settings_icon, active_icon: white_settings_icon },
];



const AppSidebar = (props: React.ComponentProps<typeof Sidebar>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const { open, toggleSidebar, openMobile, setOpenMobile } = useSidebar();

  const [confirmModal, setConfirmModal] = useState(false);

  const handleLogout = () => {
    toast.success('Logged out successfully');
    dispatch(setUser(null));
    navigate('/login');
  };

  return (
    <Sidebar collapsible='icon' className={cn('border-r transition-all duration-300')} {...props}>
      {/* Header */}
      <SidebarHeader>
        <div className={cn('flex items-center border-b px-4 py-3', open ? 'justify-between' : 'justify-center')}>
          {/* {open && <Text text='Dole Admin' className='font-semibold' size={fontSizes.h18} />} */}
          {open && <ReactSVG src={logo_icon} />}
          <Menu className='h-5 w-5 cursor-pointer shrink-0' onClick={toggleSidebar} />
        </div>
      </SidebarHeader>

      {/* Menu */}
      <SidebarContent className={cn('py-2', open ? 'px-4' : 'px-2')}>
        <SidebarMenu>
          {sidebarMenu.map((item) => {
            const active = location?.pathname === item.url;

            return (
              <SidebarMenuItem key={item.name}>
                <Link
                  // to={item?.disabled ? '' : item.url}
                  to={item.url}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 w-full mt-2 cursor-pointer',
                    active ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted',
                    !open && 'justify-center px-0',
                    // item?.disabled && 'cursor-not-allowed opacity-50',
                  )}
                  onClick={() => setOpenMobile(false)}
                >
                  <ReactSVG src={active ? item?.active_icon : item?.icon} />
                  {open && <Text text={item.name} className={cn(active ? 'text-white' : 'text-black')} />}
                </Link>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className='border-t p-3'>
        <Button
          variant='destructive'
          className={cn('w-full flex items-center gap-2', !open && 'justify-center px-0')}
          onClick={() => setConfirmModal(true)}
        >
          <LogOut className='h-5 w-5' />
          {open && <span>Logout</span>}
        </Button>
      </SidebarFooter>

      <ConfirmModal
        open={confirmModal}
        title='Logout!'
        description='Are you sure you want to logout?'
        confirmText='Yes'
        cancelText='No'
        onCancel={() => setConfirmModal(false)}
        onConfirm={handleLogout}
      />
    </Sidebar>
  );
};

export default AppSidebar;
